export interface IssueType {
    id: string;
    description: string;
    lawReference: string;
}

export interface IssueNote {
    id: string;
    issueId: string;
    createdDate: Date;
    createdByUserId: string;
    createdByUserName: string;
    text: string;
    type: number
}

export interface IssueHistory {
    id: string;
    issueId: string;
    eventDate: Date;
    description: string;
    changedByUserId: string;
    changedByUserName: string;
}

export enum EscalationStatus {
    AwaitingDecision = "",
    Approved = "Chief's Order Approved",
    Rejected = "Chief's Order Rejected"
}

export interface IssuesConfig {
    showIssuesContent: boolean;
}



export interface Issue {
    id: string;
    legacyId: string;
    organizationId: string;
    organizationNameAtTimeOfIssue: string;
    description: string;
    inspectorId: string;
    creationDate: Date;
    dateDue: Date;
    resolutionDate: Date;
    mostRecentInspectionDate: Date;
    typeId: string;
    issueOwner: string;
    wellId: string;
    wellNameAtTimeOfIssue: string;
    holdId: string;
    wasIssueContested: boolean;
    contestedReason: string;
    chiefsOrderId: string;
    letterReady: string;
    certificationNumber1stLetter: string;
    certificationNumber2ndLetter: string;

    assignee: string;
    dateOfNextAction: Date;
    contestedDate: Date;

    letter1ReceivedDate: Date;

    letter2ReceivedDate: Date;

    complianceNotice1ReceivedDate: Date;

    complianceNotice2ReceivedDate: Date;

    status: IssuesStatus;
    escalationStatus: EscalationStatus | null;

    complianceNoticeNumber: string;
    complianceNoticeIssued: Date;
    complianceNoticeDueDate: Date;
    agreferralNumber: string;
    agreferralDate: Date;
    complianceAgreementNumber: string;
    complianceAgreementDate: Date;
    complianceAgreementDueDate: Date;
    nextStep: IssueSteps;

    reportingYear: number;
    reportingQuarter: string;
    identifier: string;

    currentRICSOrganizationId: string;
    currentRICSOrganizationName: string;
    currentRBDMSOrganizationName: string;
    currentRBDMSOwnerNumber: string;


    type: IssueType;
    issueChiefsOrderTracking: IssueChiefsOrderTracking;
    issueNotices: IssueNotice[];
    issueValidations: IssueValidation[];
    issueNotes: IssueNote[];
    issueHistories: IssueHistory[];
    issueExtensions: IssueExtension[]
}

export interface IssueExtension {
    id: string;
    issueId: string;
    extendedBy: string;
    extendedOn: Date;
    previousDueDate: Date;
    extensionDate: Date;
}

export interface IssueNotice {
    id: string;
    issueId: string;
    dateDue?: Date;
    dateSent?: Date;
    datePrinted: Date;
    wasPrintedBy: string;
    name: string;
    noticeType: IssueNoticeType;
    document: string;
    dateReceived?: Date;
    expirationDate?: Date;
}

export enum IssueNoticeType {
    PreLetter = "Pre Letter",
    Letter1 = "Letter 1",
    Letter2 = "Letter 2",
    ComplianceNotice1 = "Compliance Notice 1",
    ComplianceNotice2 = "Compliance Notice 2"
}

export interface IssueValidation {
    id: string;
    issueId: string;
    isComplete: boolean;
    name: string;
    dateScanned: Date;
    documentId: string;
}

export interface IssueChiefsOrderTracking {
    issueId: string;
    isChiefsOrderReady: boolean;
    chiefsOrderCreationDate: Date;
    chiefsOrderSentDate: Date;
    chiefsOrderSignDate: Date;
    chiefsOrderDueDate: Date;
    wasChiefsOrderSelectedForPrinting: boolean;
    wasChiefsOrderValidated: boolean;
}

export enum IssuesStatus {
    Pending = "Pending",
    Validated = "Validated",
    Escalated = "Escalated",
    Contested = "Contested",
    Closed = "Closed",
    ChiefsOrderApproval = "Awaiting Chief's Order Approval"
};

export enum IssueSteps {
    KeepWorking = "Keep Working Issue",
    PreLetter = "Pre Letter",
    Letter1 = "Letter 1",
    Letter2 = "Letter 2",
    ComplianceNotice1 = "Compliance Notice 1",
    ComplianceNotice2 = "Compliance Notice 2",
    Close = "Closure - Issue Resolved",
    Escalate = "Escalate Issue"
}

export enum IssueEscalationMethods {
    ChiefsOrder = "Generate Chief's Order",
    ComplianceNotice = "Generate Compliance Notice",
    AttorneyGeneral = "Referral to AG's Office",
    ComplianceAgreement = "Generate Compliance Agreement / Settlement",
    ChiefsOrderApproved = "Chief's Order Approved",
    ChiefsOrderRejected = "Chiefs' Order Rejected"
}

export class UpdateIssueRequest {
    issueId: string;
    
    comments: IssueNote[];
    currentStep: string;
    dateOfNextAction: Date;
    escalation: IssueEscalation;
    extension: IssueExtension;
    extensionDenied: boolean;
    notices: IssueNotice[];
    ownerContested: ContestIssue;
    reassignment: IssueReassignment;
    resolvedDate: Date;
    statusChange: IssueStatusChange;

    currentRICSOrganizationId: string;
    currentRICSOrganizationName: string;
    currentRBDMSOwnerId: string;
    currentRBDMSOrganizationName: string;

    constructor(issue: Issue, targetState: IssuesStatus) {
        this.issueId = issue.id;
        this.comments = (issue.issueNotes != null) ? issue.issueNotes : [];
        this.notices = (issue.issueNotices != null) ? issue.issueNotices : [];
        if (targetState != null && issue.status !== targetState) {
            this.statusChange = { currentStatus: issue.status, newStatus: targetState } as IssueStatusChange;
        }
        this.currentRICSOrganizationId = issue.currentRICSOrganizationId;
        this.currentRICSOrganizationName = issue.currentRICSOrganizationName;
        this.currentRBDMSOwnerId = issue.currentRBDMSOwnerNumber;
        this.currentRBDMSOrganizationName = issue.currentRBDMSOrganizationName;
    }
}

export interface IssueReassignment {
    currentUser: string;
    newUser: string;
}

export interface ContestIssue {
    contested: boolean;
    contestedDate: Date;
    contestReason: string;
}

export interface IssueStatusChange {
    currentStatus: IssuesStatus;
    newStatus: IssuesStatus;
}

export interface IssueEscalation {
    escalationType: IssueEscalationMethods;
    chiefsOrderNumber: string;
    dateChiefsOrderIssued: Date;
    complianceNoticeNumber: string;
    complianceNoticeIssued: Date;
    complianceNoticeDueDate: Date;
    currentChiefsOrderEscalationStatus: string;
    referralNumber: string;
    referralDate: Date;
    complianceAgreementNumber: string;
    complianceAgreementDate: Date;
    complianceAgreementDueDate: Date;
}

export interface IssueEscalationDecision {
    approved: boolean;
    issueId: string;
    ownerName: string;
}

export interface ProgramAreaGroupMap {
    programArea: string;
    groupId: string;
}
